import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../client';

function unwrapApiResponse(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

export const useFinanceOverview = (workspaceId = '', period = '30d') => {
  return useQuery({
    queryKey: ['finance-overview', workspaceId, period],
    // The API derives organization/workspace access from the JWT. Do not send
    // workspaceId here because legacy deployments reject it in Prisma filters.
    queryFn: () => client.get('/finance/overview', { params: { period } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useInvoices = (workspaceId: string, status?: string) => {
  return useQuery({
    queryKey: ['invoices', workspaceId, status],
    queryFn: () => client.get('/invoices', { params: { workspaceId, status } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useTransactions = (workspaceId: string) => {
  return useQuery({
    queryKey: ['transactions', workspaceId],
    queryFn: () => client.get('/transactions', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAccounts = (workspaceId: string) => {
  return useQuery({
    queryKey: ['accounts', workspaceId],
    queryFn: () => client.get('/accounts', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useExpenses = (workspaceId: string) => {
  return useQuery({
    queryKey: ['expenses', workspaceId],
    queryFn: () => client.get('/expenses', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useRevenues = (workspaceId: string) => {
  return useQuery({
    queryKey: ['revenues', workspaceId],
    queryFn: () => client.get('/revenues', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post('/invoices', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};

export const useUpdateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: any) => client.put(`/invoices/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};

export const useDeleteInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/invoices/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};

export const useCreateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post('/transactions', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};

export const useCreateExpense = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post('/expenses', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};

export const useCreateRevenue = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post('/revenues', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['revenues'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
    },
  });
};
